/**
 * Property value and participation.
 *
 * Two readings of the same project: what the property is worth now against the valuation it
 * started from, and who funded that value. The amounts are deliberately separated - a participation
 * stake at the current valuation is not the capital that was invested, and a valuation movement is
 * not a return anyone has received.
 */
import React from 'react';
import { Buildings, Info, ArrowUpRight, ArrowDownRight, ArrowRight } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { money, pct, INVESTOR_BLUE, FLIPPEX_PINK } from './instrumentEconomics';

const Overline = ({ children, className = '' }) => (
  <p className={`text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted ${className}`}>
    {children}
  </p>
);

const polarPoint = (cx, cy, r, deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

const annularWedge = (cx, cy, rOuter, rInner, start, end) => {
  const large = end - start > 180 ? 1 : 0;
  const [x1, y1] = polarPoint(cx, cy, rOuter, start);
  const [x2, y2] = polarPoint(cx, cy, rOuter, end);
  const [x3, y3] = polarPoint(cx, cy, rInner, end);
  const [x4, y4] = polarPoint(cx, cy, rInner, start);
  return `M${x1} ${y1} A${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2} L${x3} ${y3} A${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4} Z`;
};

/** Compact donut; the Flippex wedge sits slightly outside the ring so the co-investment reads as separate. */
const ParticipationDonut = ({ investorPercent }) => {
  const investor = Math.max(0, Math.min(100, investorPercent || 0));
  const flippex = 100 - investor;
  const cx = 54;
  const cy = 54;
  const rOuter = 40;
  const rInner = 24;
  const investorEnd = (investor / 100) * 360;
  const [ox, oy] = polarPoint(0, 0, 6, investorEnd + (360 - investorEnd) / 2);

  return (
    <svg
      width="108"
      height="108"
      viewBox="0 0 114 114"
      className="flex-shrink-0"
      role="img"
      aria-label={`Participation split: investors ${investor.toFixed(1)}%, Flippex ${flippex.toFixed(1)}%`}
      data-testid="participation-donut"
    >
      {investor > 0 && (
        <path
          d={investor >= 99.95
            ? `${annularWedge(cx, cy, rOuter, rInner, 0, 180)} ${annularWedge(cx, cy, rOuter, rInner, 180, 360)}`
            : annularWedge(cx, cy, rOuter, rInner, 0, investorEnd)}
          fill={INVESTOR_BLUE}
        />
      )}
      {flippex > 0.05 && (
        <path
          d={annularWedge(cx, cy, rOuter, rInner, investorEnd, 360)}
          fill={FLIPPEX_PINK}
          transform={`translate(${ox} ${oy})`}
        />
      )}
    </svg>
  );
};

/** Mobile reading of the same split. */
const ParticipationBar = ({ investorPercent, flippexPercent }) => (
  <div className="flex h-2 rounded-full overflow-hidden bg-slate-100" data-testid="participation-bar">
    <div style={{ width: `${Math.max(0, Math.min(100, investorPercent || 0))}%`, backgroundColor: INVESTOR_BLUE }} />
    <div style={{ width: `${Math.max(0, Math.min(100, flippexPercent || 0))}%`, backgroundColor: FLIPPEX_PINK }} />
  </div>
);

const Stake = ({ label, percent, value, note, color, testId }) => (
  <div>
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
      <Overline className="whitespace-nowrap">{label}</Overline>
    </span>
    <p className="mt-1 text-sm md:text-base tabular-nums">
      <span className="font-semibold" style={{ color }} data-testid={testId}>{pct(percent)}</span>
      {value !== null && value !== undefined && (
        <span className="text-foreground-muted"> · {money(value)}</span>
      )}
    </p>
    {note && <p className="mt-0.5 text-[11px] md:text-xs text-foreground-muted leading-relaxed">{note}</p>}
  </div>
);

const formatValuationDate = (date) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const PropertyParticipationCard = ({
  className = '',
  currentValue,
  baselineValue,
  change = 0,
  changePct = 0,
  valuationDate,
  investorPercent,
  flippexPercent,
  investorValue,
  flippexValue,
  investedCapital,
  currency = 'EUR',
  onExplain,
}) => {
  const hasMoved = Math.round(change || 0) !== 0;
  const up = (change || 0) > 0;
  const changeColor = up ? 'text-emerald-600' : 'text-red-500/90';
  const ChangeIcon = up ? ArrowUpRight : ArrowDownRight;
  const valuedOn = valuationDate ? formatValuationDate(valuationDate) : null;
  const hasFlippex = (flippexPercent || 0) > 0.05;

  return (
    <section className={className} data-testid="property-participation-card">
      <div className="flex items-center gap-2">
        <Buildings size={17} className="text-primary" weight="duotone" />
        <h3 className="text-sm md:text-base font-heading">Property Value &amp; Participation</h3>
      </div>

      <div className="mt-5 md:mt-6 md:grid md:grid-cols-2 md:gap-8 lg:gap-10">
        {/* Property valuation */}
        <div data-testid="property-valuation-block">
          <Overline>Property valuation</Overline>
          <p
            className="mt-2 text-[28px] md:text-[38px] leading-none font-semibold tracking-tight text-slate-900 tabular-nums"
            data-testid="current-property-value"
          >
            {money(currentValue, currency)}
          </p>

          <div className="mt-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="inline-flex items-baseline gap-1 text-xs md:text-sm text-foreground-muted underline decoration-dotted decoration-slate-300 underline-offset-2"
                  data-testid="current-property-value-term"
                >
                  Current property value
                  <Info size={12} className="text-foreground-muted translate-y-[1px]" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 text-xs leading-relaxed" data-testid="current-property-value-tooltip">
                <span className="block text-[10px] uppercase tracking-[0.14em] text-foreground-muted mb-1.5">
                  How this is valued
                </span>
                The project&apos;s net asset value. It is set when the instrument is issued, from the acquisition price
                and the capital expenditure budget, and updated when a new valuation or a material change in the
                project is recorded. It is a valuation of the property, not cash held, not an amount distributed, and
                not a return received by investors. A change in the property valuation is not a realized return:
                investors receive proceeds on a distribution or at exit, net of project costs.
              </PopoverContent>
            </Popover>
          </div>

          {hasMoved ? (
            <p className={`mt-3.5 inline-flex items-center gap-1.5 text-sm md:text-base font-semibold ${changeColor} tabular-nums`} data-testid="valuation-change">
              <ChangeIcon size={15} weight="bold" />
              {up ? '+' : '−'}{money(Math.abs(change), currency)} ({up ? '+' : '−'}{pct(Math.abs(changePct))})
            </p>
          ) : (
            <p className="mt-3.5 text-xs md:text-sm text-foreground-muted" data-testid="valuation-change">
              No change since the initial valuation
            </p>
          )}

          {baselineValue ? (
            <p className="mt-1.5 text-[11px] md:text-xs text-foreground-muted tabular-nums">
              against an initial valuation of {money(baselineValue, currency)}
              {valuedOn ? ` · last valued ${valuedOn}` : ''}
            </p>
          ) : valuedOn ? (
            <p className="mt-1.5 text-[11px] md:text-xs text-foreground-muted">Last valued {valuedOn}</p>
          ) : null}
        </div>

        {/* Investment participation */}
        <div className="mt-6 pt-5 border-t border-black/[0.06] md:mt-0 md:pt-0 md:border-t-0 md:border-l md:border-black/[0.06] md:pl-8 lg:pl-10" data-testid="participation-block">
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="inline-flex items-baseline gap-1"
                data-testid="investment-participation-term"
              >
                <Overline>Investment participation</Overline>
                <Info size={12} className="text-foreground-muted translate-y-[1px]" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 text-xs leading-relaxed" data-testid="investment-participation-tooltip">
              <span className="block text-[10px] uppercase tracking-[0.14em] text-foreground-muted mb-1.5">
                How participation is measured
              </span>
              Participation is each side&apos;s share of the property value, fixed when the instrument is issued. The
              amounts shown are that share at the current valuation, not capital returned.
            </PopoverContent>
          </Popover>

          {/* Desktop: donut. Mobile: segmented bar. */}
          <div className="mt-4 hidden lg:flex lg:items-center lg:gap-6">
            <ParticipationDonut investorPercent={investorPercent} />
            <div className="min-w-0 space-y-4">
              <Stake
                label="Investors"
                percent={investorPercent}
                value={investorValue}
                color={INVESTOR_BLUE}
                testId="investor-participation"
                note={investedCapital ? `${money(investedCapital, currency)} invested` : null}
              />
              <Stake
                label="Flippex"
                percent={flippexPercent}
                value={hasFlippex ? flippexValue : null}
                color={FLIPPEX_PINK}
                testId="flippex-participation"
                note={hasFlippex ? 'Co-invested capital' : 'No co-investment'}
              />
            </div>
          </div>

          <div className="mt-4 lg:hidden">
            <div className="flex items-baseline justify-between gap-3 tabular-nums">
              <span className="text-base font-semibold" style={{ color: INVESTOR_BLUE }} data-testid="investor-participation-mobile">
                {pct(investorPercent)}
              </span>
              <span className="text-base font-semibold" style={{ color: FLIPPEX_PINK }} data-testid="flippex-participation-mobile">
                {pct(flippexPercent)}
              </span>
            </div>
            <div className="mt-2">
              <ParticipationBar investorPercent={investorPercent} flippexPercent={flippexPercent} />
            </div>
            <div className="mt-2 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Overline>Investors</Overline>
                <p className="text-xs text-foreground-muted tabular-nums mt-0.5">{money(investorValue)}</p>
                {investedCapital ? (
                  <p className="text-[11px] text-foreground-muted tabular-nums">{money(investedCapital, currency)} invested</p>
                ) : null}
              </div>
              <div className="min-w-0 text-right">
                <Overline>Flippex</Overline>
                <p className="text-xs text-foreground-muted tabular-nums mt-0.5">
                  {hasFlippex ? money(flippexValue) : 'None'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investing alongside you */}
      <div className="mt-6 pt-5 border-t border-black/[0.06] sm:flex sm:items-start sm:justify-between sm:gap-8">
        <div className="min-w-0">
          <Overline>{hasFlippex ? 'Investing alongside you' : 'How your participation is set'}</Overline>
          <p className="text-xs md:text-sm text-foreground-muted leading-relaxed mt-1.5">
            {hasFlippex
              ? 'Flippex commits its own capital alongside investors, aligning our interests in the project\u2019s success.'
              : 'Investors fund the full property value on this project. Participation is fixed when the instrument is issued and is not diluted by later valuation changes.'}
          </p>
        </div>
        <div className="mt-3 flex justify-end sm:mt-0 sm:block sm:flex-shrink-0">
          <button
            onClick={onExplain}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-70 active:opacity-60 transition-opacity"
            data-testid="participation-explainer-btn"
          >
            How participation works
            <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default PropertyParticipationCard;
